import {Component, OnDestroy, OnInit} from '@angular/core';
import {BroadcastsService} from "../services/broadcasts.service";
import {UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule} from "@angular/forms";
import {catchError, take} from "rxjs/operators";
import {GamesService} from "../services/games.service";
import {Game, PublishedDay, ScheduledDay, ScheduleGame} from "../model/broadcast/game";
import {LeaguesService} from "../services/leagues.service";
import {League, TeamDefaultConfig} from "../model/league";
import {of, scheduled, Subscription} from "rxjs";
import {SchedulesService} from "../services/schedules.service";
import { Timestamp } from "firebase/firestore";
import {MatDialogRef, MatDialogModule} from "@angular/material/dialog";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatButtonModule} from "@angular/material/button";
import {MatTabsModule} from "@angular/material/tabs";
import {MatRadioModule} from "@angular/material/radio";
import {MatIconModule} from "@angular/material/icon";
import {MatSelectModule} from "@angular/material/select";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {NgIf, NgFor, DatePipe} from "@angular/common";
import {MatCheckbox} from "@angular/material/checkbox";

@Component({
    selector: 'app-broadcast-dialog',
    templateUrl: './broadcast-dialog.component.html',
    styleUrls: ['./broadcast-dialog.component.scss'],
    standalone: true,
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatTabsModule,
    MatRadioModule,
    MatIconModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    NgIf,
    NgFor,
    DatePipe,
    MatCheckbox
  ]
})
export class BroadcastDialogComponent implements OnInit, OnDestroy {

  formData: UntypedFormGroup;
  loading: boolean;
  publishedGames: Game[] = [];
  publishedDays: PublishedDay[] = [];
  scheduledGames: ScheduleGame[] = [];
  scheduledDays: ScheduledDay[] = [];
  scheduleLoading = false;
  scheduleError = false;
  leagues: League[] = [];
  leagueGroups: { federation: string; leagues: League[] }[] = [];
  teams: TeamDefaultConfig[] = [];
  leagueControlSub: Subscription;
  leaguesSub: Subscription;

  constructor(
    public dialogRef: MatDialogRef<BroadcastDialogComponent>,
    public broadcastService: BroadcastsService,
    public gamesService: GamesService,
    public leaguesService: LeaguesService,
    public schedulesService: SchedulesService,
  ) { }

  ngOnInit(): void {
    console.log('getting all games');
    this.gamesService.getUnfinishedGames().pipe(take(1)).subscribe(games => {
      console.log('got all games');
      const now = Timestamp.now().toDate(); // Current date
      const startRange = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 14); // 7 days before today
      const endRange = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1); // 7 days after today

      this.publishedGames = games.filter(g => !g.finished && g.scheduled && g.scheduled.toDate() >= startRange && g.scheduled.toDate() <= endRange);
      this.publishedGames.forEach(g => {
        if (!this.publishedDays.find(d => d.date.toDate().toDateString() == g.scheduled.toDate().toDateString())) {
          this.publishedDays.push({date: g.scheduled, games: []});
        }
        this.publishedDays.find(d => d.date.toDate().toDateString() == g.scheduled.toDate().toDateString()).games.push(g);
      });
      this.publishedDays.sort((a, b) => a.date.toDate().getTime() - b.date.toDate().getTime());
      console.log('parsed published games into model');
      this.leaguesSub = this.leaguesService.getLeagues().subscribe(leagues => {
        this.leagues = leagues;
        this.buildLeagueGroups();
        console.log('got leagues from firestore, creating form....');
        if (!this.formData) {
          this.createForm();
        }
      });
    });
  }

  ngOnDestroy() {
    if (this.leagueControlSub) {
      this.leagueControlSub.unsubscribe();
    }
    if (this.leaguesSub) {
      this.leaguesSub.unsubscribe();
    }
  }

  private buildLeagueGroups(): void {
    const map = new Map<string, League[]>();
    for (const l of this.leagues) {
      const fed = l.federation?.trim() || 'Other';
      if (!map.has(fed)) map.set(fed, []);
      map.get(fed).push(l);
    }
    this.leagueGroups = Array.from(map.entries())
      .map(([federation, leagues]) => ({
        federation,
        leagues: leagues.sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.federation.localeCompare(b.federation));
  }

  cancel(): void {
    this.dialogRef.close();
  }

  ok(): void {
    if (this.formData.invalid) {
      return;
    }
    this.loading = true;
    const autopilot: boolean = this.formData.get('autopilot').value;
    if (this.formData.controls.game.enabled) {
      const game: Game = this.formData.get('game').value;
      const league = this.leagues.find(l => l.id == game.tournamentId);
      let home = league?.teams.find(t => t.id == game.home.guid);
      if (!home) {
        home = {fullName: game.home.shortName, id: game.home.guid, logoUrl: '/assets/logos/ball-score/1h105.png', primaryColor: '#172743', shortName: game.home.shortName};
      }
      let away = league?.teams.find(t => t.id == game.away.guid);
      if (!away) {
        away = {fullName: game.away.shortName, id: game.away.guid, logoUrl: '/assets/logos/ball-score/1h105.png', primaryColor: '#D6252B', shortName: game.away.shortName};;
      }
      this.submit(this.broadcastService.createBroadcast(league, home, away, game.gameId, false));
    } else if (this.formData.controls.league.enabled) {
      const league: League = this.formData.get('league').value;
      const scheduledGame: ScheduleGame = this.formData.get('scheduledGame').value;
      if (scheduledGame) {
        const home: TeamDefaultConfig = league.teams.find(t => t.id == scheduledGame.home.id);
        const away: TeamDefaultConfig = league.teams.find(t => t.id == scheduledGame.away.id);
        this.submit(this.broadcastService.createBroadcast(league, home, away, scheduledGame.gameId, autopilot, scheduledGame.scheduled));
      } else {
        // Manual fallback: schedule unavailable, pick teams from the league and
        // optionally enter the game id so live stats are still pulled.
        const home: TeamDefaultConfig = this.formData.get('homeTeam').value;
        const away: TeamDefaultConfig = this.formData.get('awayTeam').value;
        if (!home || !away) {
          this.loading = false;
          return;
        }
        const gameId: string = (this.formData.get('gameId').value || '').trim() || null;
        this.submit(this.broadcastService.createBroadcast(league, home, away, gameId, autopilot && !!gameId));
      }
    } else {
      const home = {fullName: 'Home team', id: 'home', logoUrl: '/assets/logos/ball-score/1h105.png', primaryColor: '#172743', shortName: 'HOM'};
      const away = {fullName: 'Away team', id: 'away', logoUrl: '/assets/logos/ball-score/1h105.png', primaryColor: '#D6252B', shortName: 'GUE'};
      this.submit(this.broadcastService.createBroadcast(null, home, away, null, false));
    }
  }

  private submit(promise: Promise<string>): void {
    promise.then(uid => {
      this.loading = false;
      this.dialogRef.close(uid);
    }, error => {
      console.log('catch in modal');
      console.log(error);
      this.loading = false;
    });
  }
  public typeChange(index: number) {
    this.formData.reset();
    this.formData.disable();
    this.teams = [];
    this.scheduledDays = [];
    this.scheduledGames = [];
    this.scheduleLoading = false;
    this.scheduleError = false;
    if (index === 0) {
      this.formData.controls.game.reset();
      this.formData.controls.game.disable();
      this.formData.controls.league.enable();
      this.formData.controls.autopilot.enable();
      //this.formData.controls.scheduledGame.enable();
    } else if(index === 1) {
      this.formData.controls.game.enable();
    }
  }

  private createForm(): void {
    this.formData = new UntypedFormGroup({
      game: new UntypedFormControl('', [Validators.required]),
      scheduledGame: new UntypedFormControl(''),
      homeTeam: new UntypedFormControl(''),
      awayTeam: new UntypedFormControl(''),
      gameId: new UntypedFormControl(''),
      league: new UntypedFormControl('', [Validators.required]),
      autopilot: new UntypedFormControl('true'),
    });

    this.formData.controls.game.disable();
    this.formData.controls.scheduledGame.disable();
    this.formData.controls.homeTeam.disable();
    this.formData.controls.awayTeam.disable();
    this.formData.controls.gameId.disable();

    this.leagueControlSub = this.formData.controls.league.valueChanges.subscribe(value => {
      if (value?.url) {
        console.log('got league selected ' + value?.name);
        // League teams come from Firestore, so manual home/away selection works
        // even when the WBSC schedule scrape is unavailable.
        this.teams = [...(value.teams || [])].sort((a, b) => a.fullName.localeCompare(b.fullName));
        this.formData.controls.homeTeam.enable();
        this.formData.controls.awayTeam.enable();
        this.formData.controls.gameId.enable();
        this.formData.controls.scheduledGame.disable();
        this.scheduledDays = [];
        this.scheduledGames = [];
        this.scheduleError = false;
        this.scheduleLoading = true;
        this.schedulesService.getSchedule(value.id).pipe(
          take(1),
          catchError(err => {
            console.log('failed to fetch schedule, falling back to manual team selection', err);
            this.scheduleError = true;
            return of([] as ScheduleGame[]);
          })
        ).subscribe(games => {
          this.scheduleLoading = false;
          console.log('got ' + value.name + ' schedule');
          let visibleGames = games;
          if (!value.licenced) {
            const licencedTeamIds = (value.teams || []).filter(t => t.licenced).map(t => t.id);
            visibleGames = games.filter(g => licencedTeamIds.includes(g.home.id));
          }
          this.scheduledDays = [];
          visibleGames.forEach(g => {
            if (!this.scheduledDays.find(d => g.scheduled.toDate().toDateString() == d.date.toDate().toDateString())) {
              this.scheduledDays.push({date: g.scheduled, games: []});
            }
            this.scheduledDays.find(d => g.scheduled.toDate().toDateString() == d.date.toDate().toDateString()).games.push(g);
          });
          this.scheduledGames = visibleGames;
          if (visibleGames.length) {
            this.formData.controls.scheduledGame.enable();
          }
          console.log('parsed ' + value.name + ' schedule');
          const nowMillis: number = Timestamp.now().toMillis()
          const defaultGame: ScheduleGame = visibleGames.sort((a,b) => {
            return Math.abs(b.scheduled.toMillis() - nowMillis) - Math.abs(a.scheduled.toMillis() - nowMillis);
          }).pop();
          if (defaultGame) {
            this.formData.controls.scheduledGame.patchValue(defaultGame);
            console.log('set default game ' + defaultGame.gameNumber);
          }
        });
      }
    });
  }
}
